<template>
  <ul
    v-if="listFunctRoomSetup.length > 1"
    class="db-room-setup-list"
    ref="listDetailsIconsWrapper"
  >
    <li
      v-for="(roomSetup, index) in visibleItems"
      :key="roomSetup.id"
      class="db-room-container-setup"
      :id="`db-room-container-setup-list-${roomSetup.id}-${index}`"
    >
      <span
        class="db-room-item-setup-icon"
        v-html="roomSetup.default_icon"
      />
      <div class="db-room-item-setup-name">{{ roomSetup.name }}</div>
      <span class="db-visually-hidden">{{ roomSetup.name }}</span>
    </li>
    <li
      v-if="
        (!showAll &&
          listFunctRoomSetup.length > 1 &&
          listFunctRoomSetup.length > visibleItems.length) ||
        (showAll &&
          listFunctRoomSetup.length > 1 &&
          listFunctRoomSetup.length === visibleItems.length)
      "
      type="button"
      v-on:click="toggleShowAll"
      class="db-room-container-setup-show-more"
      ref="listDetailsIconsShowMore"
    >
      <button class="db-room-item-setup-show-more">
        <IconDbChevron
          width="10"
          height="10"
          class="db-chevron"
          :class="{
            'db-rotate-180': !showAll,
          }"
        />
      </button>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'

type itemFunctRoomSetup = {
  id: string | number
  cle: string
  name: string
  /** HTML for the svg of the icon */
  default_icon: string | undefined
}

const listDetailsIconsWrapper = ref(undefined)
const listDetailsIconsShowMore = ref(undefined)
const showAll = ref(false)
const visibleItems = ref<itemFunctRoomSetup[]>([])

const props = defineProps<{
  listFunctRoomSetup: itemFunctRoomSetup[]
  dynamicWidthWithCallBack?: () => {}
}>()

const computeVisibleItems = () => {
  // If no functionRoomSetup is defined, do nothing
  if (!props.listFunctRoomSetup || props.listFunctRoomSetup.length === 0) {
    return
  }
  // In this case, always put default value
  if (props.listFunctRoomSetup.length <= 1) {
    visibleItems.value = props.listFunctRoomSetup.slice(0, 1)
    return
  }
  // If all the functRoom should be displayed
  if (showAll.value) {
    visibleItems.value = props.listFunctRoomSetup
  } else {
    // Or only the first line
    const container = listDetailsIconsWrapper.value
    const containerWidth = container?.offsetWidth || 250 // default size
    const showmore = listDetailsIconsShowMore.value
    const showmoreWidth = showmore?.offsetWidth || 30 // default size
    const gapSpacing = 6
    visibleItems.value = props.listFunctRoomSetup.slice(0, 1)
    // Can't compute if one of these failed
    if (!container || !containerWidth || !showmoreWidth) {
      return
    }
    let counterElementToBeDisplayed = 0
    let counterCurrSize = showmoreWidth + gapSpacing // default value is at least one
    for (const [index, itemFunctRoom] of props.listFunctRoomSetup.entries()) {
      // Get roomSetup width
      let roomSetup = document.getElementById(
        `db-room-container-setup-list-${itemFunctRoom.id}-${index}`,
      )
      // If roomSetup is not defined
      if (!roomSetup || !roomSetup.offsetWidth) {
        // Estimated this possible size
        counterCurrSize += 56 + Number(7 * itemFunctRoom.name.length) + gapSpacing
      } else {
        // Put the roomSetup size
        counterCurrSize += roomSetup.offsetWidth + gapSpacing
      }
      // Check that this is not too long
      if (counterCurrSize >= containerWidth) {
        break
      }
      // Add this new roomSetup
      counterElementToBeDisplayed++
    }
    visibleItems.value = props.listFunctRoomSetup.slice(0, counterElementToBeDisplayed || 1) // default value is at least one
  }
}

let resizeObserver = null

function handleResize() {
  computeVisibleItems()
}

onMounted(() => {
  nextTick(() => {
    handleResize()
    resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(listDetailsIconsWrapper.value)
  })
})

const toggleShowAll = () => {
  if (props.dynamicWidthWithCallBack) {
    props.dynamicWidthWithCallBack()
  } else {
    showAll.value = !showAll.value
  }
  handleResize()
}
</script>

<style>
.db-app .db-room-setup-list {
  display: flex;
  flex-wrap: wrap;
  column-gap: 6px;
  row-gap: 4px;
}

.db-app .db-room-container-setup {
  background-color: var(--db_cards_background_color);
  color: var(--db_iconography_color);
  min-height: 36px;
  border: var(--db_borders_color) solid 0.5px;
  border-radius: 5px;
  padding: 0px 7px;
  display: flex;
}

.db-app .db-room-container-setup .db-room-item-setup-icon {
  color: var(--db_iconography_color);
  width: 28px;
  height: 100%;
  margin-right: 4px;
  display: flex;
}

.db-app .db-room-container-setup .db-room-item-setup-icon svg {
  color: var(--db_iconography_color);
  width: 22px !important;
  height: 22px !important;
  margin: auto;
  width: 100%;
}

.db-app .db-room-container-setup .db-room-item-setup-name {
  color: var(--db_iconography_color);
  font-weight: 600;
  font-size: var(--db_small_body_text_font_size);
  width: max-content;
  height: 100%;
  display: flex;
  align-items: center;
}

.db-app .db-room-container-setup-show-more {
  background-color: var(--db_cards_background_color);
  color: var(--db_link_color);
  min-height: 36px;
  border: var(--db_link_color) solid 0.5px;
  border-radius: 5px;
  padding: 0px 8px;
  display: flex;
  cursor: pointer;
}

.db-app .db-room-container-setup-show-more:hover {
  border: var(--db_link_hover_color) solid 0.5px;
  color: var(--db_link_hover_color);
}

.db-app .db-room-container-setup-show-more .db-room-item-setup-show-more {
  display: flex;
  margin: auto;
  cursor: pointer;
}

.db-app .db-room-container-setup-show-more .db-room-item-setup-show-more svg {
  width: 10px;
  height: 10px;
  color: var(--db_link_color);
  fill: var(--db_link_color) !important;
  cursor: pointer;
}
</style>
